import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, forkJoin, map, of, switchMap, timer, withLatestFrom } from 'rxjs';

import {
  IDirectAssignmentContacts,
  IHQTicketDetailsResponse,
  IMetadataTicketDetailsResponse,
  IRootQuestionTicketOverview,
  ITicketEditResponse,
  ITicketMessageGroup,
  ITicketResidentCategory,
  ITicketResidentOverviewResponse,
  TicketIssueType,
  TicketStatus,
} from '../../models';
import { contractStatusConverter } from '../../services/account/masterdata/contract-status.converter';
import { ErrorHandlerService } from '../../services/error-handler/error-handler.base-service';
import { ticketChatConverter } from '../../services/tickets/ticket-chat.converter';
import { TicketsFacade } from '../../services/tickets/tickets.facade';
import * as fromMasterDataState from '../account/masterdata';
import * as fromReducers from '../account/masterdata/masterdata.reducer';
import * as fromTicketState from './';
import * as fromActions from './tickets.actions';
import * as fromTicketReducers from './tickets.reducer';
import { mergeMap } from 'rxjs/operators';
import { getPermissions } from '../account';
import { getTicketChat } from './';
import { getTicketSorting } from '../general/sortings/sorting.selectors';

const CHAT_POLL_INTERVAL = 5000; // 5 secs

@Injectable()
export class TicketsEffects {
  loadDamageTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadDamageTickets),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContract)),
      switchMap(([action, contract]) =>
        this.ticketsFacade
          .getTicketsOverview(
            contract?.id ? [contract.id] : [],
            action.issueTypes,
            action.archived,
            action.offset,
            action.limit,
            action.sort
          )
          .pipe(
            map((response: ITicketResidentOverviewResponse) =>
              fromActions.LoadDamageTicketsSuccess({
                response,
              })
            ),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadDamageTickets$:',
                error
              );
              return [fromActions.LoadDamageTicketsFailed({ error })];
            })
          )
      )
    )
  );
  loadRequestTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadRequestTickets),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContract)),
      switchMap(([action, contract]) =>
        this.ticketsFacade
          .getTicketsOverview(
            contract?.id ? [contract.id] : [],
            action.issueTypes,
            action.archived,
            action.offset,
            action.limit,
            action.sort
          )
          .pipe(
            map((response: ITicketResidentOverviewResponse) =>
              fromActions.LoadRequestTicketsSuccess({
                response,
              })
            ),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadRequestTickets$:',
                error
              );
              return [fromActions.LoadRequestTicketsFailed({ error })];
            })
          )
      )
    )
  );
  loadDamageCategoryQuestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadDamageCategoryQuestions),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContract)),

      switchMap(([_, contract]) =>
        this.ticketsFacade
          .getCategoryQuestions(
            TicketIssueType.PROPERTY_DAMAGE,
            contract && contractStatusConverter.isContractActive(contract) ? contract.id : undefined
          )
          .pipe(
            map((damageCategories: IRootQuestionTicketOverview) =>
              fromActions.LoadDamageCategoryQuestionsSuccess({
                damageCategories,
              })
            ),
            catchError(error => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadDamageCategoryQuestions$:',
                error
              );
              return [fromActions.LoadDamageCategoryQuestionsFailed({ error })];
            })
          )
      )
    )
  );
  loadConcernCategoryQuestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadConcernCategoryQuestions),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContract)),

      switchMap(([_, contract]) =>
        this.ticketsFacade
          .getCategoryQuestions(
            TicketIssueType.CONCERN,
            contract && contractStatusConverter.isContractActive(contract) ? contract.id : undefined
          )
          .pipe(
            map((concernCategories: IRootQuestionTicketOverview) =>
              fromActions.LoadConcernCategoryQuestionsSuccess({
                concernCategories,
              })
            ),
            catchError(error => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadConcernCategoryQuestions$:',
                error
              );
              return [fromActions.LoadConcernCategoryQuestionsFailed({ error })];
            })
          )
      )
    )
  );
  loadDamageQuestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadDamageQuestions),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContract)),
      switchMap(([action, contract]) =>
        this.ticketsFacade
          .getDetailedQuestions(
            action.damageCategoryQuestions,
            TicketIssueType.PROPERTY_DAMAGE,
            contract && contractStatusConverter.isContractActive(contract) ? contract.id : undefined
          )
          .pipe(
            map((damageQuestions: ITicketResidentCategory) => {
              if (!damageQuestions) {
                damageQuestions = this.createEmptyQuestionObject();
              }
              return fromActions.LoadDamageQuestionsSuccess({
                damageQuestions,
              });
            }),
            catchError(error => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadDamageQuestions$:',
                error
              );
              return [fromActions.LoadDamageQuestionsFailed({ error })];
            })
          )
      )
    )
  );
  loadConcernQuestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadConcernQuestions),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContract)),
      switchMap(([action, contract]) =>
        this.ticketsFacade
          .getDetailedQuestions(
            action.concernCategoryQuestions,
            TicketIssueType.CONCERN,
            contract && contractStatusConverter.isContractActive(contract) ? contract.id : undefined
          )
          .pipe(
            map((concernQuestions: ITicketResidentCategory) => {
              if (!concernQuestions) {
                concernQuestions = this.createEmptyQuestionObject();
              }
              return fromActions.LoadConcernQuestionsSuccess({
                concernQuestions,
              });
            }),
            catchError(error => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadConcernQuestions$:',
                error
              );
              return [fromActions.LoadConcernQuestionsFailed({ error })];
            })
          )
      )
    )
  );
  public loadActiveDamageTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadActiveDamageTickets),
      withLatestFrom(this.store.select(fromMasterDataState.getContracts)),
      switchMap(([action, contracts]) => {
        const contractIds = contracts.map(c => c.id);
        const issueTypes: TicketIssueType[] = [TicketIssueType.PROPERTY_DAMAGE];

        return this.ticketsFacade
          .getTicketsOverview(
            contractIds,
            issueTypes,
            false,
            action.offset,
            action.limit,
            action.sort
          )
          .pipe(
            map(ticketsOverview => {
              return fromActions.LoadActiveDamageTicketsSuccess({
                sort: action.sort,
                response: [ticketsOverview],
              });
            }),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadActiveDamageTickets$:',
                error
              );
              return of(fromActions.LoadActiveDamageTicketsFailed({ error }));
            })
          );
      })
    )
  );

  loadActiveDamageTicketsHQ$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadActiveDamageTicketsHQ),
      withLatestFrom(this.store.select(fromMasterDataState.getContracts)),
      switchMap(([action, contracts]) =>
        forkJoin(
          contracts.map(c =>
            forkJoin([
              this.ticketsFacade
                .getTicketsOverviewHQ(
                  c?.id,
                  TicketIssueType.PROPERTY_DAMAGE,
                  TicketStatus.OPEN,
                  action.offset,
                  action.limit,
                  action.sort
                )
                .pipe(catchError(e => this.processError(e))),
              this.ticketsFacade
                .getTicketsOverviewHQ(
                  c?.id,
                  TicketIssueType.PROPERTY_DAMAGE,
                  TicketStatus.IN_PROGRESS,
                  action.offset,
                  action.limit,
                  action.sort
                )
                .pipe(catchError(e => this.processError(e))),
              this.ticketsFacade
                .getTicketsOverviewHQ(
                  c?.id,
                  TicketIssueType.PROPERTY_DAMAGE,
                  TicketStatus.WAITING_FOR_OTHERS,
                  action.offset,
                  action.limit,
                  action.sort
                )
                .pipe(catchError(e => this.processError(e))),
            ]).pipe(
              map(([openTickets, inProgressTickets, waitingForOthersTickets]) => [
                openTickets,
                inProgressTickets,
                waitingForOthersTickets,
              ])
            )
          )
        ).pipe(
          map(responses =>
            fromActions.LoadActiveDamageTicketsSuccess({
              sort: action.sort,
              response: responses.reduce(
                (accumulator, currentValue) => accumulator.concat(currentValue),
                []
              ),
            })
          ),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on tickets.effects.ts:loadActiveDamageTicketsHQ$:',
              error
            );
            return [fromActions.LoadActiveDamageTicketsFailed({ error })];
          })
        )
      )
    )
  );

  loadActiveDamageTicketsForSelectedContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadActiveDamageTicketsForSelectedContract),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContractForTicketsOverview)),
      switchMap(([action, contract]) => {
        const issueTypes: TicketIssueType[] = [TicketIssueType.PROPERTY_DAMAGE];

        return this.ticketsFacade
          .getTicketsOverview(
            contract?.id ? [contract.id] : [],
            issueTypes,
            false,
            action.offset,
            action.limit,
            action.sort
          )
          .pipe(
            map(overview =>
              fromActions.LoadActiveDamageTicketsForSelectedContractSuccess({
                sort: action.sort,
                response: [overview],
              })
            ),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadActiveDamageTicketsForSelectedContract$:',
                error
              );
              return of(
                fromActions.LoadActiveDamageTicketsForSelectedContractFailed({
                  error,
                })
              );
            })
          );
      })
    )
  );

  loadActiveDamageTicketsForSelectedContractHQ$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadActiveDamageTicketsForSelectedContractHQ),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContractForTicketsOverview)),
      switchMap(([action, contract]) =>
        forkJoin([
          this.ticketsFacade
            .getTicketsOverviewHQ(
              contract?.id,
              TicketIssueType.PROPERTY_DAMAGE,
              TicketStatus.OPEN,
              action.offset,
              action.limit,
              action.sort
            )
            .pipe(catchError(e => this.processError(e))),
          this.ticketsFacade
            .getTicketsOverviewHQ(
              contract?.id,
              TicketIssueType.PROPERTY_DAMAGE,
              TicketStatus.IN_PROGRESS,
              action.offset,
              action.limit,
              action.sort
            )
            .pipe(catchError(e => this.processError(e))),
          this.ticketsFacade
            .getTicketsOverviewHQ(
              contract?.id,
              TicketIssueType.PROPERTY_DAMAGE,
              TicketStatus.WAITING_FOR_OTHERS,
              action.offset,
              action.limit,
              action.sort
            )
            .pipe(catchError(e => this.processError(e))),
        ])
          .pipe(
            map(([openTickets, inProgressTickets, waitingForOthersTickets]) => [
              openTickets,
              inProgressTickets,
              waitingForOthersTickets,
            ])
          )
          .pipe(
            map(responses =>
              fromActions.LoadActiveDamageTicketsForSelectedContractSuccess({
                sort: action.sort,
                response: responses.reduce(
                  (accumulator, currentValue) => accumulator.concat(currentValue),
                  []
                ),
              })
            ),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadActiveDamageTicketsForSelectedContractHQ$:',
                error
              );
              return [fromActions.LoadActiveDamageTicketsForSelectedContractFailed({ error })];
            })
          )
      )
    )
  );

  loadArchivedDamageTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadArchiveDamageTickets),
      withLatestFrom(this.store.select(fromMasterDataState.getContracts)),
      switchMap(([action, contracts]) => {
        const contractIds: string[] = contracts.map(c => c.id);
        const issueTypes: TicketIssueType[] = [TicketIssueType.PROPERTY_DAMAGE];

        return this.ticketsFacade
          .getTicketsOverview(
            contractIds,
            issueTypes,
            true,
            action.offset,
            action.limit,
            action.sort
          )
          .pipe(
            map(overview =>
              fromActions.LoadArchiveDamageTicketsSuccess({
                sort: action.sort,
                response: [overview],
              })
            ),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadArchivedDamageTickets$:',
                error
              );
              return of(fromActions.LoadArchiveDamageTicketsFailed({ error }));
            })
          );
      })
    )
  );

  public loadArchivedDamageTicketsForSelectedContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadArchiveDamageTicketsForSelectedContract),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContractForTicketsOverview)),
      switchMap(([action, contract]) => {
        const issueTypes: TicketIssueType[] = [TicketIssueType.PROPERTY_DAMAGE];

        return this.ticketsFacade
          .getTicketsOverview(
            contract?.id ? [contract.id] : [],
            issueTypes,
            true,
            action.offset,
            action.limit,
            action.sort
          )
          .pipe(
            map(overview =>
              fromActions.LoadArchiveDamageTicketsForSelectedContractSuccess({
                sort: action.sort,
                response: [overview],
              })
            ),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadArchivedDamageTicketsForSelectedContract$:',
                error
              );
              return of(
                fromActions.LoadArchiveDamageTicketsForSelectedContractFailed({
                  error,
                })
              );
            })
          );
      })
    )
  );

  loadArchivedDamageTicketsForSelectedContractHQ$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadArchiveDamageTicketsForSelectedContractHQ),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContractForTicketsOverview)),
      switchMap(([action, contract]) =>
        forkJoin([
          this.ticketsFacade
            .getTicketsOverviewHQ(
              contract?.id,
              TicketIssueType.PROPERTY_DAMAGE,
              TicketStatus.CLOSED,
              action.offset,
              action.limit,
              action.sort
            )
            .pipe(catchError(e => this.processError(e))),
          this.ticketsFacade
            .getTicketsOverviewHQ(
              contract?.id,
              TicketIssueType.PROPERTY_DAMAGE,
              TicketStatus.CANCELLED,
              action.offset,
              action.limit,
              action.sort
            )
            .pipe(catchError(e => this.processError(e))),
        ])
          .pipe(map(([closedTickets, cancelledTickets]) => [closedTickets, cancelledTickets]))
          .pipe(
            map(responses =>
              fromActions.LoadArchiveDamageTicketsForSelectedContractSuccess({
                sort: action.sort,
                response: responses.reduce(
                  (accumulator, currentValue) => accumulator.concat(currentValue),
                  []
                ),
              })
            ),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadArchivedDamageTicketsForSelectedContractHQ$:',
                error
              );
              return [fromActions.LoadArchiveDamageTicketsForSelectedContractFailed({ error })];
            })
          )
      )
    )
  );

  loadActiveRequestTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadActiveRequestTickets),
      withLatestFrom(this.store.select(fromMasterDataState.getContracts)),
      switchMap(([action, contracts]) => {
        const contractIds = contracts.map(c => c.id);
        const issueTypes = [TicketIssueType.CONCERN];

        return this.ticketsFacade
          .getTicketsOverview(
            contractIds,
            issueTypes,
            false,
            action.offset,
            action.limit,
            action.sort
          )
          .pipe(
            map(ticketsOverview =>
              fromActions.LoadActiveRequestTicketsSuccess({
                sort: action.sort,
                response: [ticketsOverview],
              })
            ),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadActiveRequestTickets$:',
                error
              );
              return of(fromActions.LoadActiveRequestTicketsFailed({ error }));
            })
          );
      })
    )
  );

  loadActiveRequestTicketsHQ$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadActiveRequestTicketsHQ),
      withLatestFrom(this.store.select(fromMasterDataState.getContracts)),
      switchMap(([action, contracts]) =>
        forkJoin(
          contracts.map(c =>
            forkJoin([
              this.ticketsFacade
                .getTicketsOverviewHQ(
                  c?.id,
                  TicketIssueType.CONCERN,
                  TicketStatus.OPEN,
                  action.offset,
                  action.limit,
                  action.sort
                )
                .pipe(catchError(e => this.processError(e))),
              this.ticketsFacade
                .getTicketsOverviewHQ(
                  c?.id,
                  TicketIssueType.CONCERN,
                  TicketStatus.IN_PROGRESS,
                  action.offset,
                  action.limit,
                  action.sort
                )
                .pipe(catchError(e => this.processError(e))),
              this.ticketsFacade
                .getTicketsOverviewHQ(
                  c?.id,
                  TicketIssueType.CONCERN,
                  TicketStatus.WAITING_FOR_OTHERS,
                  action.offset,
                  action.limit,
                  action.sort
                )
                .pipe(catchError(e => this.processError(e))),
            ]).pipe(
              map(([openTickets, inProgressTickets, waitingForOthersTickets]) => [
                openTickets,
                inProgressTickets,
                waitingForOthersTickets,
              ])
            )
          )
        ).pipe(
          map(responses =>
            fromActions.LoadActiveRequestTicketsSuccess({
              sort: action.sort,
              response: responses.reduce(
                (accumulator, currentValue) => accumulator.concat(currentValue),
                []
              ),
            })
          ),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on tickets.effects.ts:loadActiveRequestTicketsHQ$:',
              error
            );
            return [fromActions.LoadActiveRequestTicketsFailed({ error })];
          })
        )
      )
    )
  );

  loadActiveRequestTicketsForSelectedContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadActiveRequestTicketsForSelectedContract),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContractForTicketsOverview)),
      switchMap(([action, contract]) => {
        const issueTypes: TicketIssueType[] = [TicketIssueType.CONCERN];

        return this.ticketsFacade
          .getTicketsOverview(
            contract?.id ? [contract.id] : [],
            issueTypes,
            false,
            action.offset,
            action.limit,
            action.sort
          )
          .pipe(
            map(overview =>
              fromActions.LoadActiveRequestTicketsForSelectedContractSuccess({
                sort: action.sort,
                response: [overview],
              })
            ),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadActiveRequestTicketsForSelectedContract$:',
                error
              );
              return of(fromActions.LoadActiveRequestTicketsForSelectedContractFailed({ error }));
            })
          );
      })
    )
  );

  loadActiveRequestTicketsForSelectedContractHQ$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadActiveRequestTicketsForSelectedContractHQ),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContractForTicketsOverview)),
      switchMap(([action, contract]) =>
        forkJoin([
          this.ticketsFacade
            .getTicketsOverviewHQ(
              contract?.id,
              TicketIssueType.CONCERN,
              TicketStatus.OPEN,
              action.offset,
              action.limit,
              action.sort
            )
            .pipe(catchError(e => this.processError(e))),
          this.ticketsFacade
            .getTicketsOverviewHQ(
              contract?.id,
              TicketIssueType.CONCERN,
              TicketStatus.IN_PROGRESS,
              action.offset,
              action.limit,
              action.sort
            )
            .pipe(catchError(e => this.processError(e))),
          this.ticketsFacade
            .getTicketsOverviewHQ(
              contract?.id,
              TicketIssueType.CONCERN,
              TicketStatus.WAITING_FOR_OTHERS,
              action.offset,
              action.limit,
              action.sort
            )
            .pipe(catchError(e => this.processError(e))),
        ])
          .pipe(
            map(([openTickets, inProgressTickets, waitingForOthersTickets]) => [
              openTickets,
              inProgressTickets,
              waitingForOthersTickets,
            ])
          )
          .pipe(
            map(responses => {
              return fromActions.LoadActiveRequestTicketsForSelectedContractSuccess({
                sort: action.sort,
                response: responses.reduce(
                  (accumulator, currentValue) => accumulator.concat(currentValue),
                  []
                ),
              });
            }),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadActiveRequestTicketsForSelectedContractHQ$:',
                error
              );
              return [fromActions.LoadActiveRequestTicketsForSelectedContractFailed({ error })];
            })
          )
      )
    )
  );

  loadArchiveRequestTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadArchiveRequestTickets),
      withLatestFrom(this.store.select(fromMasterDataState.getContracts)),
      switchMap(([action, contracts]) => {
        const contractIds: string[] = contracts.map(c => c.id);
        const issueTypes: TicketIssueType[] = [TicketIssueType.CONCERN];

        return this.ticketsFacade
          .getTicketsOverview(
            contractIds,
            issueTypes,
            true,
            action.offset,
            action.limit,
            action.sort
          )
          .pipe(
            map(overview =>
              fromActions.LoadArchiveRequestTicketsSuccess({
                sort: action.sort,
                response: [overview],
              })
            ),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadArchiveRequestTickets$:',
                error
              );
              return of(fromActions.LoadArchiveRequestTicketsFailed({ error }));
            })
          );
      })
    )
  );

  loadArchiveRequestTicketsForSelectedContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadArchiveRequestTicketsForSelectedContract),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContractForTicketsOverview)),
      switchMap(([action, contract]) => {
        const issueTypes: TicketIssueType[] = [TicketIssueType.CONCERN];

        return this.ticketsFacade
          .getTicketsOverview(
            contract?.id ? [contract.id] : [],
            issueTypes,
            true,
            action.offset,
            action.limit,
            action.sort
          )
          .pipe(
            map(overview =>
              fromActions.LoadArchiveRequestTicketsForSelectedContractSuccess({
                sort: action.sort,
                response: [overview],
              })
            ),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:loadArchiveRequestTicketsForSelectedContract$:',
                error
              );
              return of(fromActions.LoadArchiveRequestTicketsForSelectedContractFailed({ error }));
            })
          );
      })
    )
  );

  loadArchiveRequestTicketsForSelectedContractHQ$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadArchiveRequestTicketsForSelectedContractHQ),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContractForTicketsOverview)),
      switchMap(([action, contract]) =>
        forkJoin([
          this.ticketsFacade
            .getTicketsOverviewHQ(
              contract?.id,
              TicketIssueType.CONCERN,
              TicketStatus.CLOSED,
              action.offset,
              action.limit,
              action.sort
            )
            .pipe(catchError(e => this.processError(e))),
          this.ticketsFacade
            .getTicketsOverviewHQ(
              contract?.id,
              TicketIssueType.CONCERN,
              TicketStatus.CANCELLED,
              action.offset,
              action.limit,
              action.sort
            )
            .pipe(catchError(e => this.processError(e))),
        ])
          .pipe(map(([closedTickets, cancelledTickets]) => [closedTickets, cancelledTickets]))
          .pipe(
            map(responses => {
              return fromActions.LoadArchiveRequestTicketsForSelectedContractSuccess({
                sort: action.sort,
                response: responses.reduce(
                  (accumulator, currentValue) => accumulator.concat(currentValue),
                  []
                ),
              });
            }),
            catchError((error: Error) => {
              this.errorHandlerService.handleError(
                '(REQUEST ERROR) on tickets.effects.ts:LoadArchiveRequestTicketsForSelectedContractHQ$:',
                error
              );
              return [fromActions.LoadArchiveRequestTicketsForSelectedContractFailed({ error })];
            })
          )
      )
    )
  );

  loadTicketDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadTicketDetails),
      switchMap(({ ticketId, ticketIssueType }) =>
        this.ticketsFacade.getTicketDetailsHQ(ticketId, ticketIssueType).pipe(
          map((ticketDetailsResponse: IHQTicketDetailsResponse) =>
            fromActions.LoadTicketDetailsSuccess({ response: ticketDetailsResponse })
          ),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on tickets.effects.ts:loadTicketDetails$:',
              error
            );
            return [fromActions.LoadTicketDetailsFailed({ error })];
          })
        )
      )
    )
  );
  createTicketHQ$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreateTicketHQ),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContract)),
      switchMap(([action, contract]) => {
        const newTicket = { ...action.newTicketHQ, contractNumber: contract.id };
        return this.ticketsFacade.createTicketHQ(newTicket).pipe(
          map((createTicketResponse: ITicketEditResponse) =>
            fromActions.CreateTicketHQSuccess({ createTicketResponse })
          ),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on tickets.effects.ts:createTicketHQ$:',
              error
            );
            return [fromActions.CreateTicketHQFailed({ error })];
          })
        );
      })
    )
  );
  cancelTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CancelTicket),
      switchMap(({ ticketId, ticketIssueType }) =>
        this.ticketsFacade.cancelTicket(ticketId, ticketIssueType).pipe(
          map((cancelTicket: boolean) =>
            fromActions.CancelTicketSuccess({
              cancelTicketResponse: cancelTicket,
              ticketId,
              ticketIssueType,
            })
          ),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on tickets.effects.ts:cancelTicket$:',
              error
            );
            return [fromActions.LoadTicketDetailsFailed({ error })];
          })
        )
      )
    )
  );

  archiveTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.ArchiveTicket),
      switchMap(({ ticketId, ticketIssueType }) =>
        this.ticketsFacade.archiveTicket(ticketId).pipe(
          map(() => fromActions.ArchiveTicketSuccess({ ticketId, ticketIssueType })),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on tickets.effects.ts:archiveTicket$:',
              error
            );
            return [fromActions.ArchiveTicketFailed({ error })];
          })
        )
      )
    )
  );

  archiveTicketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.ArchiveTicketSuccess),
      mergeMap(({ ticketIssueType }) => {
        const sort = this.store.selectSignal(getTicketSorting)();
        const actions: Action[] = [];
        const data = {
          offset: 0,
          limit: 20,
          sort,
        };

        if (ticketIssueType === 'PROPERTY_DAMAGE') {
          actions.push(
            fromActions.LoadActiveDamageTicketsForSelectedContract(data),
            fromActions.LoadArchiveDamageTickets(data)
          );
        } else {
          actions.push(
            fromActions.LoadActiveRequestTicketsForSelectedContract(data),
            fromActions.LoadArchiveRequestTickets(data)
          );
        }
        return actions;
      })
    )
  );

  loadTicketChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadTicketChat),
      withLatestFrom(this.store.select(fromMasterDataState.getUser)),
      switchMap(([action, user]) =>
        this.ticketsFacade.getTicketChat(action.ticketId).pipe(
          map(ticketMessages =>
            ticketChatConverter.fromDto(
              ticketMessages.edges.map(conv => conv.node),
              user
            )
          ),
          map((ticketMessageGroups: ITicketMessageGroup[]) =>
            fromActions.LoadTicketChatSuccess({ ticketMessageGroups })
          ),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on tickets.effects.ts:loadTicketChat$:',
              error
            );
            return [fromActions.LoadTicketChatFailed({ error })];
          })
        )
      )
    )
  );

  /**
   * Updates the ticket chat based on new messages
   * @returns UpdateTicketChatSuccess | UpdateTicketChatFailed
   * TODO: Figure out with BE for a better solution that doesn't require polling
   */
  updateTicketChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.UpdateTicketChat),
      withLatestFrom(
        this.store.select(fromMasterDataState.getUser),
        this.store.select(getTicketChat)
      ),
      switchMap(([action, user, messageGroups]) =>
        this.ticketsFacade.getTicketChat(action.ticketId).pipe(
          map(ticketMessages => {
            const newChat = ticketChatConverter.fromDto(
              ticketMessages.edges.map(conv => conv.node),
              user
            );
            const updatedGroups = ticketChatConverter.appendNewMessagesToChat(
              messageGroups,
              newChat
            );
            return fromActions.UpdateTicketChatSuccess({ ticketMessageGroups: updatedGroups });
          }),
          catchError((error: Error) => of(fromActions.UpdateTicketChatFailed({ error })))
        )
      )
    )
  );

  addMessageToTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.AddMessageToTicket),
      withLatestFrom(
        this.ticketStore.select(fromTicketState.getTicketChat),
        this.store.select(fromMasterDataState.getUser)
      ),
      switchMap(([action, messageGroups, user]) =>
        this.ticketsFacade.addMessageToTicket(action.message).pipe(
          withLatestFrom(this.store.select(getPermissions)),
          mergeMap(([newMessage, permissions]) => {
            const ticketMessageGroups = ticketChatConverter.toNewMessage(
              newMessage.createdMessageId,
              action.message,
              [...messageGroups],
              user,
              permissions.internalTicketing
            );
            const actions: Action[] = [
              fromActions.AddMessageToTicketSuccess({ ticketMessageGroups }),
            ];

            if (!permissions.internalTicketing) {
              actions.push(fromActions.LoadTicketChat({ ticketId: action.message.ticketId }));

              const hasPendingMessage = ticketChatConverter.hasPendingMessage(ticketMessageGroups);

              if (hasPendingMessage) {
                actions.push(fromActions.PollTicketChat({ ticketId: action.message.ticketId }));
              }
            }

            return actions;
          }),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on tickets.effects.ts:addMessageToTicket$:',
              error
            );
            return [fromActions.AddMessageToTicketFailed({ error })];
          })
        )
      )
    )
  );

  pollTicketChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.PollTicketChat),
      switchMap(({ ticketId }) =>
        timer(CHAT_POLL_INTERVAL).pipe(map(() => fromActions.LoadTicketChat({ ticketId })))
      )
    )
  );

  loadMetadataTicketDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadMetadataTicketDetails),
      switchMap(({ ticketId, ticketIssueType }) =>
        this.ticketsFacade.getTicketDetailsMetadata(ticketId, ticketIssueType).pipe(
          map((metadataTicketDetailsResponse: IMetadataTicketDetailsResponse) =>
            fromActions.LoadMetadataTicketDetailsSuccess({
              response: metadataTicketDetailsResponse,
            })
          ),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on tickets.effects.ts:loadMetadataTicketDetails$:',
              error
            );
            return [fromActions.LoadMetadataTicketDetailsFailed({ error })];
          })
        )
      )
    )
  );
  createTicketMetadata$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreateTicketMetadata),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContract)),
      switchMap(([action, contract]) => {
        const newTicket = {
          ...action.newTicketMetadata,
          contractNumber:
            contract && contractStatusConverter.isContractActive(contract)
              ? contract.id
              : undefined,
        };
        return this.ticketsFacade.createTicketMetadata(newTicket).pipe(
          map((createTicketResponse: ITicketEditResponse) =>
            fromActions.CreateTicketMetadataSuccess({ createTicketResponse })
          ),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on tickets.effects.ts:createTicketMetadata$:',
              error
            );
            return [fromActions.CreateTicketMetadataFailed({ error })];
          })
        );
      })
    )
  );
  loadDirectAssignmentContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadDirectAssignmentContacts),
      withLatestFrom(this.store.select(fromMasterDataState.getSelectedContract)),
      switchMap(([action, contract]) => {
        return this.ticketsFacade.getDirectAssignments(action.category, contract.id).pipe(
          map((response: IDirectAssignmentContacts) =>
            fromActions.LoadDirectAssignmentContactsSuccess({
              directAssignmentContacts: response.directAssignmentContacts,
            })
          ),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on tickets.effects.ts:loadDirectAssignmentContacts$:',
              error
            );
            return [fromActions.LoadDirectAssignmentContactsError({ error })];
          })
        );
      })
    )
  );

  getCategoryDetailQuestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadCategoryDetailQuestions),
      switchMap(({ ticketCategoryId }) =>
        this.ticketsFacade.getCategoryDetailQuestions(ticketCategoryId).pipe(
          map((categoryDetailQuestions: IRootQuestionTicketOverview) => {
            return fromActions.LoadCategoryDetailQuestionsSuccess({
              categoryDetailQuestions,
            });
          }),
          catchError(error => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on tickets.effects.ts:loadDamageCategoryQuestions$:',
              error
            );
            return [fromActions.LoadCategoryDetailQuestionsFailed({ error })];
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private ticketsFacade: TicketsFacade,
    private store: Store<fromReducers.IMasterDataState>,
    private ticketStore: Store<fromTicketReducers.ITicketsState>,
    private errorHandlerService: ErrorHandlerService
  ) {}

  processError(e: Error) {
    console.error(e);
    return of({
      edges: [],
      pageInfo: { hasPreviousPage: false, hasNextPage: false, startCursor: null, endCursor: null },
    });
  }

  createEmptyQuestionObject(): ITicketResidentCategory {
    return {
      id: null,
      categoryAnswerSetRelationId: '',
      name: '',
      detailRootQuestion: {
        id: null,
        mainQuestionIds: [],
        questions: [],
        meta: [],
      },
    };
  }
}
